import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useAuthStore } from '@/stores/authStore';
import { useCourtesyAvailability, useCourtesyVehicles } from '@/hooks/useCourtesyVehicles';
import { courtesyVehicleLabel, type CreateCourtesySelection } from './courtesyAfterCreate';

interface CourtesyVehicleSelectProps {
  /** Default loan window (#4094) — null while the schedule is incomplete. */
  plannedStart: string | null;
  plannedEnd: string | null;
  value: CreateCourtesySelection | null;
  onChange: (value: CreateCourtesySelection | null) => void;
}

/**
 * Optional courtesy-car pick in the create wizard (#4103). The loan itself is
 * reserved after the work order is saved, for the window shown here. Hidden
 * when the workshop has no active courtesy cars or the user cannot reserve.
 */
export function CourtesyVehicleSelect({
  plannedStart,
  plannedEnd,
  value,
  onChange,
}: CourtesyVehicleSelectProps) {
  const { t } = useTranslation('workorders');
  // Reserving a loan needs work_orders.update (staff-api POST /courtesy-loans).
  const canReserve = useAuthStore((s) => s.hasPermission('work_orders.update'));
  const { data: cars } = useCourtesyVehicles({ isActive: true });
  const hasCars = (cars?.length ?? 0) > 0;

  const rangeValid =
    !!plannedStart && !!plannedEnd && new Date(plannedEnd).getTime() > new Date(plannedStart).getTime();
  const enabled = canReserve && hasCars && rangeValid;
  const { data: availability, isLoading } = useCourtesyAvailability(
    enabled ? (plannedStart ?? undefined) : undefined,
    enabled ? (plannedEnd ?? undefined) : undefined,
  );
  const freeCars = (availability ?? []).filter((car) => car.isActive && car.available);

  // A schedule change can take the picked car out of the free list: drop the
  // pick rather than reserve a car that would be refused after save.
  const selectedId = value?.id ?? null;
  const availabilityLoaded = availability !== undefined;
  const selectedStillFree = freeCars.some((car) => car.id === selectedId);
  useEffect(() => {
    if (selectedId && availabilityLoaded && !selectedStillFree) onChange(null);
  }, [selectedId, availabilityLoaded, selectedStillFree, onChange]);

  if (!canReserve || !hasCars) return null;

  return (
    <Box sx={{ mt: 2 }} data-testid="create-courtesy-vehicle">
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('courtesy.vehicle')}
      </Typography>
      {rangeValid && !isLoading && availabilityLoaded && freeCars.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {t('courtesy.noFreeCars')}
        </Alert>
      )}
      <FormControl fullWidth size="small" disabled={!rangeValid || isLoading}>
        <InputLabel id="create-courtesy-vehicle-label">{t('courtesy.createLabel')}</InputLabel>
        <Select
          labelId="create-courtesy-vehicle-label"
          label={t('courtesy.createLabel')}
          value={selectedStillFree && selectedId ? selectedId : ''}
          onChange={(event) => {
            const id = String(event.target.value);
            const car = freeCars.find((c) => c.id === id);
            onChange(car ? { id: car.id, label: courtesyVehicleLabel(car) } : null);
          }}
        >
          <MenuItem value="">
            <em>{t('courtesy.createNone')}</em>
          </MenuItem>
          {freeCars.map((car) => (
            <MenuItem key={car.id} value={car.id}>
              {courtesyVehicleLabel(car)}
            </MenuItem>
          ))}
        </Select>
        {rangeValid && plannedStart && plannedEnd && (
          <FormHelperText>
            {t('courtesy.createWindow', {
              start: format(new Date(plannedStart), 'd.M.yyyy HH:mm'),
              end: format(new Date(plannedEnd), 'd.M.yyyy HH:mm'),
            })}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
}
